import React from 'react'
import PropTypes from 'prop-types'
import momentPropTypes from 'react-moment-proptypes'
import moment from 'moment'
import pluralize from 'pluralize'
import { Calendar, Clock, Users, GraduationCap } from 'lucide-react'

import Avatar from '../../leevo_ui/Avatar/Avatar'
import { Badge } from '../../ui/badge'
import { levelArrayToString } from '../../../utilities/helpers'

function CoachHeader({ coaches, classStartDateTime, skillLevels, numStudents }) {
  const skillLevelNames = Array.isArray(skillLevels)
    ? skillLevels
    : skillLevels?.map((level) => level.name || level) || []

  const hasCoaches = coaches && coaches.length > 0

  return (
    <header className="p-6 mb-6 bg-white border border-gray-200 rounded-lg shadow-sm">
      <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
        <div className="flex-1 space-y-4">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 p-2 bg-gray-100 rounded-lg">
              <Calendar className="w-5 h-5 text-gray-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Date & Time</p>
              <p className="text-lg font-semibold text-gray-900">
                {moment(classStartDateTime).format('MMMM D, YYYY [at] h:mm A')}
              </p>
            </div>
          </div>

          {skillLevelNames.length > 0 && (
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 p-2 bg-gray-100 rounded-lg">
                <GraduationCap className="w-5 h-5 text-gray-600" />
              </div>
              <div className="flex-1">
                <p className="mb-2 text-sm font-medium text-gray-500">Enrolled Skill Levels</p>
                <div className="flex flex-wrap gap-2">
                  {skillLevelNames.map((level, index) => (
                    <Badge key={`${level}-${index}`} variant="secondary">
                      {level}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          )}

          {numStudents !== undefined && numStudents !== null && (
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 p-2 bg-gray-100 rounded-lg">
                <Users className="w-5 h-5 text-gray-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Enrollment</p>
                <p className="text-lg font-semibold text-gray-900">{pluralize('student', numStudents, true)}</p>
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-3 pt-6 border-t border-gray-200 md:pt-0 md:border-t-0 md:border-l md:px-6 md:min-w-[240px]">
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-gray-600" />
            <p className="text-sm font-semibold text-gray-900">
              {hasCoaches ? (coaches.length === 1 ? 'Coach' : 'Coaches') : 'Coach'}
            </p>
          </div>
          {hasCoaches ? (
            <div className="flex flex-col gap-4">
              {coaches.map(({ preferredName, cloudinaryPhotoPublicId, id, fullName }) => (
                <div key={id} className="flex items-center gap-3">
                  <Avatar
                    fullName={fullName || preferredName || 'Coach'}
                    size={cloudinaryPhotoPublicId ? 16 : 12}
                    cloudinaryPhotoPublicId={cloudinaryPhotoPublicId}
                    userId={id}
                    className="flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-base font-semibold text-gray-900 truncate" data-cy="coach-name">
                      {preferredName || fullName || 'Coach Name Not Set'}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-12 h-12 bg-gray-100 border-2 border-gray-200 rounded-full flex-shrink-0">
                <Users className="w-6 h-6 text-gray-400" />
              </div>
              <div className="flex-1">
                <p className="text-base font-semibold text-gray-500">Not Assigned</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  )
}
CoachHeader.propTypes = {
  classStartDateTime: momentPropTypes.momentObj,
  coaches: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      preferredName: PropTypes.string.isRequired,
      cloudinaryPhotoPublicId: PropTypes.string
    })
  ).isRequired,
  skillLevels: PropTypes.arrayOf(PropTypes.string).isRequired,
  numStudents: PropTypes.number
}

export default CoachHeader
